import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  Button,
  Flex,
  Skeleton as AntSkeleton,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import styled from '@emotion/styled';

import DisplayValue from 'components/DisplayValue/DisplayValue';

import PastDueHistoryModal from './CreditDetailsSection/PastDueHistoryModal';

import useAccountQuery from 'hooks/useAccountQuery';
import useModalControls from 'hooks/useModalControls';
import {
  formatDollarsFromCents,
  getMostDelinquentCycleMinDue,
} from 'utilities/helpers';
import { getDueDate } from 'utilities/datesAndTimes';
import { formatDate } from 'utilities/datesAndTimes';
import { BREAKPOINT } from 'utilities/constants';
import { WarningOutlined } from '@ant-design/icons';
import { styledWithTransientProps } from 'utilities/styles';
import DueDateChangeModal from './CreditDetailsSection/DueDateChangeModal';

const { Text } = Typography;

const SnapshotGrid = styled.div`
  display: grid;

  @media (min-width: ${BREAKPOINT.SM_PX}) {
    grid-gap: var(--spacing-xs) var(--spacing-xs);
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (min-width: ${BREAKPOINT.XL_PX}) {
    grid-gap: 0 var(--spacing-xs);
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  }
`;

const Skeleton = styled(AntSkeleton)`
  padding-top: var(--spacing-xxs);

  .ant-skeleton-paragraph {
    margin-bottom: 0;
  }
`;

const SnapshotValueContainer = styledWithTransientProps('div')(({ alert }) => {
  return `
    border: var(--border-secondary);
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow-tertiary);
    overflow: hidden;
    padding: var(--spacing-xs) var(--spacing-sm);
    position: relative;
    background-color: ${alert ? 'var(--color-warning-background)' : 'inherit'};
  `;
});

const SnapshotValue = ({
  children,
  label,
  alert,
  tooltipText,
  loading,
  canCopy,
  copiedValue,
}) => {
  let snapshot = (
    <SnapshotValueContainer alert={alert || undefined}>
      <DisplayValue label={label} canCopy={canCopy} copiedValue={copiedValue}>
        {loading ? (
          <Skeleton
            active={true}
            loading={true}
            title={false}
            paragraph={{ rows: 1 }}
          ></Skeleton>
        ) : (
          children
        )}
      </DisplayValue>
    </SnapshotValueContainer>
  );

  if (tooltipText) {
    snapshot = <Tooltip title={tooltipText}>{snapshot}</Tooltip>;
  }

  return snapshot;
};

const CreditAccountSnapshot = ({ showAutopayModal = () => {} }) => {
  const { accountId, customerId } = useParams();
  const { accountQuery, account } = useAccountQuery(customerId, accountId);
  const loading = accountQuery.loading;

  const dueDateChangeModal = useModalControls();
  const pastDueHistoryModal = useModalControls();

  const [dueDateChangeEligibility, dueDateChangeEligibilityFailure] =
    useMemo(() => {
      let eligibility;
      let failure = false;

      if (account) {
        eligibility = account.dueDate?.changeEligibilityChecklist;

        if (eligibility === null) {
          failure = true;
        }
      }

      return [eligibility, failure];
    }, [account]);

  const getMinimumDueProps = () => {
    const minDue = account.paymentInfo?.minimumDue;
    const currentBalance = account.balanceInfo?.currentBalance;
    const minDueOverBalance = minDue > currentBalance;

    return {
      label: 'Minimum Due',
      alert: minDueOverBalance,
      tooltipText:
        minDueOverBalance && 'Current balance is less than minimum due',
    };
  };

  const pastDueBalance = account?.balanceInfo?.pastDueBalance;
  const isPastDue = pastDueBalance > 0;
  const mostDelinquentCycleMinDue = getMostDelinquentCycleMinDue(account);

  const currentDueDate = getDueDate(account?.paymentInfo);

  const statementStartDate = dayjs(
    account?.paymentInfo?.lastStatementCloseDate,
  ).add(1, 'day');

  return (
    <>
      <DueDateChangeModal
        {...dueDateChangeModal}
        dueDate={currentDueDate}
        dueDateChangeEligibility={dueDateChangeEligibility}
        loadingDueDate={loading}
        showAutopayModal={showAutopayModal}
        statementStartDate={statementStartDate}
        statementEndDate={account?.paymentInfo?.nextStatementCloseDate}
      />
      <PastDueHistoryModal
        {...pastDueHistoryModal}
        pastDue={account?.pastDue}
      />

      <SnapshotGrid>
        <SnapshotValue
          label="Balance"
          loading={loading}
          canCopy={true}
          copiedValue={formatDollarsFromCents(
            account.balanceInfo?.currentBalance,
          )}
        >
          {formatDollarsFromCents(account.balanceInfo?.currentBalance)}
        </SnapshotValue>

        <SnapshotValue
          loading={loading}
          {...getMinimumDueProps()}
          canCopy={true}
          copiedValue={formatDollarsFromCents(account.paymentInfo?.minimumDue)}
        >
          {formatDollarsFromCents(account.paymentInfo?.minimumDue)}
        </SnapshotValue>

        <SnapshotValue
          label="Past Due Amount"
          loading={loading}
          alert={isPastDue}
        >
          <Flex
            align="center"
            justify="space-between"
            wrap="wrap"
            gap="var(--spacing-xxs)"
          >
            <div>{`${formatDollarsFromCents(pastDueBalance)}`}</div>
            <Button
              type="link"
              size="small"
              onClick={pastDueHistoryModal.show}
              style={{
                padding: 0,
              }}
            >
              History
            </Button>
          </Flex>
          {isPastDue && mostDelinquentCycleMinDue && (
            <Text
              type="secondary"
              style={{
                fontSize: 'var(--font-size-sm)',
                whiteSpace: 'normal',
              }}
            >
              Most Delinquent Cycle Min Due:{' '}
              {formatDollarsFromCents(mostDelinquentCycleMinDue)}
            </Text>
          )}
        </SnapshotValue>

        <SnapshotValue label="Available Credit" loading={loading}>
          {formatDollarsFromCents(account.balanceInfo?.availableCredit)}
        </SnapshotValue>

        <SnapshotValue label="Due Date" loading={loading}>
          <Flex
            align="center"
            justify="space-between"
            wrap="wrap"
            gap="var(--spacing-xxs)"
          >
            <div>{currentDueDate}</div>
            {dueDateChangeEligibilityFailure ? (
              <Tooltip title="Failed to retrieve due date change eligibility checklist">
                <Text type="danger">Change</Text>
              </Tooltip>
            ) : (
              <Button
                type="link"
                size="small"
                onClick={dueDateChangeModal.show}
                style={{
                  padding: 0,
                }}
              >
                Change:{' '}
                {dueDateChangeEligibility?.isEligible
                  ? 'Eligible'
                  : 'Ineligible'}
              </Button>
            )}
          </Flex>
        </SnapshotValue>

        <SnapshotValue label="Statement Period" loading={loading}>
          {formatDate(statementStartDate)} -{' '}
          {formatDate(account?.paymentInfo?.nextStatementCloseDate)}
        </SnapshotValue>
      </SnapshotGrid>
    </>
  );
};

export default CreditAccountSnapshot;
